import { useEffect, useRef } from 'react';
import { useAuthenticationStore } from './use-authentication-store';
import { refreshAccessToken } from '@/services/auth-client';
import { decodeJwtExpiryMs } from '@/utils/jwt';

// Refresh this long before the access token actually expires, so a slow
// request in flight doesn't get cut off by expiry mid-call.
const REFRESH_MARGIN_MS = 60_000;
// Fallback delay if the token's exp claim can't be read for some reason —
// avoids a tight retry loop rather than assuming it's already expired.
const FALLBACK_DELAY_MS = 5 * 60_000;

// Proactively refreshes the Supabase access token before it expires (~1h
// lifetime) using the refresh token captured at login/signup/OAuth, instead
// of letting API calls start failing once it lapses. Mount once near the
// app root (see AuthProvider) — reschedules itself after every successful
// refresh (the effect re-runs because `token.token` changes), and signs the
// user out if the refresh token itself has gone bad, which ProtectedBox
// then turns into a redirect to /auth/signin.
export function useTokenRefresh() {
  const token = useAuthenticationStore(state => state.token.token);
  const refreshToken = useAuthenticationStore(state => state.refreshToken);
  const updateAccessToken = useAuthenticationStore(
    state => state.updateAccessToken,
  );
  const unauthenticate = useAuthenticationStore(state => state.unauthenticate);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
    if (!token || !refreshToken) return;

    const expiryMs = decodeJwtExpiryMs(token);
    const delay = expiryMs
      ? Math.max(expiryMs - Date.now() - REFRESH_MARGIN_MS, 0)
      : FALLBACK_DELAY_MS;

    timerRef.current = setTimeout(async () => {
      try {
        const resp = await refreshAccessToken(refreshToken);
        updateAccessToken(resp.access_token, resp.refresh_token);
      } catch {
        unauthenticate();
      }
    }, delay);

    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [token, refreshToken, updateAccessToken, unauthenticate]);
}
